/**
 * The words a pack teaches for its verbs (D229, E15) — what its vocabulary
 * declares BEYOND the base it extends, canonical token by canonical token.
 *
 * @remarks
 * HELP lists a directive by its canonical token, and the base vocabulary's
 * aliases stay off the listing: `pick up`, `grab` and `collect` are
 * conveniences for `take`, and a line that listed them all would be a
 * thesaurus. A pack's own words are different in kind. Fever Ground declares
 * `put` and `seal` on `use` because `put the comb in the jar` and `seal the
 * jar` are the sentences the game is built around; `use the comb on the
 * jar` is the engine's shape, not the island's. Those words are the game's
 * design vocabulary, and a HELP that hides them behind `use` is a help that
 * does not say how the game is played — the play-test's E15.
 *
 * So the words are the pack's aliases on an entry, less whatever the base
 * already attached to the same canonical token (a pack re-declaring `ask`
 * with the base's own list copied in teaches nothing new), plus nothing for
 * the canonical token itself, which the listing carries anyway. With no base
 * there is nothing beyond it, and the map is empty: the extra words exist to
 * surface what a base listing hides, and a lone vocabulary hides nothing.
 * Built at load with the base in hand, because the merged lexicon has
 * forgotten where each word came from.
 *
 * @packageDocumentation
 */

import type { LexiconDefinition } from '@orpheus/content-schema';

/**
 * The words the pack teaches for each of its verbs, beyond the base's.
 *
 * @param packLexicon - The pack's own vocabulary (the overlay)
 * @param baseLexicon - The base it extends, when it extends one
 * @returns Canonical token → the pack's words for it, in authored order; tokens the pack adds no word to are absent
 */
export const packVerbWordsOf = (
	packLexicon: LexiconDefinition,
	baseLexicon: LexiconDefinition | undefined,
): ReadonlyMap<string, readonly string[]> => {
	const packVerbWords = new Map<string, readonly string[]>();

	if (baseLexicon === undefined) {
		return packVerbWords;
	}

	const baseAliasesByToken = new Map(baseLexicon.intents.map((intent) => [intent.canonicalToken, new Set(intent.aliases)]));

	for (const intent of packLexicon.intents) {
		const baseAliases = baseAliasesByToken.get(intent.canonicalToken);
		const ownWords = intent.aliases.filter((alias) => !(baseAliases?.has(alias) ?? false));

		if (ownWords.length > 0) {
			packVerbWords.set(intent.canonicalToken, ownWords);
		}
	}

	return packVerbWords;
};
